import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FilterSectionConfig } from 'src/components/MobileFilterMenu';
import { LiveTrainingCardProps } from 'src/components/LiveTrainingCard';

// Filter constants
export const STATUS_FILTERS = {
  ENROLLED: 'Enrolled',
  COMPLETED: 'Completed',
  SAVED: 'Saved',
} as const;

export const TYPE_FILTERS = {
  PUBLIC: 'Public',
  PRIVATE: 'Private',
} as const;

export const DATE_FILTERS = {
  UPCOMING: 'Upcoming',
  PAST: 'Past',
} as const;

const QUERY_PARAM_KEYS = {
  STATUS: 'status',
  TYPE: 'type',
  DATE: 'date',
} as const;

interface FilterItem {
  isSelected: boolean;
  isDisabled: boolean;
}

export type LiveTrainingSession = LiveTrainingCardProps & {
  courseId: string;
  instanceId: string;
  courseMaterialsUrl: string;
};

interface UseLiveTrainingFiltersReturn {
  // Selected values
  selectedStatusFilters: string[];
  selectedTypeFilters: string[];
  selectedDateFilters: string[];
  selectedFiltersLength: number;
  hasActiveFilters: boolean;

  // Filter items for dropdowns
  statusFilterItems: Record<string, FilterItem>;
  typeFilterItems: Record<string, FilterItem>;
  dateFilterItems: Record<string, FilterItem>;

  // Handlers
  handleStatusFilterChange: (label: string, isCurrentlySelected: boolean) => void;
  handleTypeFilterChange: (label: string, isCurrentlySelected: boolean) => void;
  handleDateFilterChange: (label: string, isCurrentlySelected: boolean) => void;
  handleClearFilters: () => void;

  // Mobile config
  mobileFilterSections: FilterSectionConfig[];
}

export const useLiveTrainingFilters = (liveTrainingSessions: LiveTrainingSession[]): UseLiveTrainingFiltersReturn => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get current filter values from URL
  const getSelectedFilters = (key: string): string[] => {
    const param = searchParams.get(key);

    return param ? param.split(',') : [];
  };

  const selectedStatusFilters = getSelectedFilters(QUERY_PARAM_KEYS.STATUS);
  const selectedTypeFilters = getSelectedFilters(QUERY_PARAM_KEYS.TYPE);
  const selectedDateFilters = getSelectedFilters(QUERY_PARAM_KEYS.DATE);

  // Calculate total number of selected filters
  const selectedFiltersLength = selectedStatusFilters.length + selectedTypeFilters.length + selectedDateFilters.length;

  // Check if any filters are active
  const hasActiveFilters =
    selectedStatusFilters.length > 0 || selectedTypeFilters.length > 0 || selectedDateFilters.length > 0;

  // Calculate counts for each filter option
  const filterCounts = useMemo(() => {
    const now = new Date();

    return {
      enrolled: liveTrainingSessions.filter((s) => s.enrolled && s.status === 'upcoming').length,
      completed: liveTrainingSessions.filter((s) => s.status === 'completed').length,
      saved: liveTrainingSessions.filter((s) => s.isSaved).length,
      public: liveTrainingSessions.filter((s) => !s.isPrivate).length,
      private: liveTrainingSessions.filter((s) => s.isPrivate).length,
      upcoming: liveTrainingSessions.filter((s) => new Date(s.endDateTime) >= now).length,
      past: liveTrainingSessions.filter((s) => new Date(s.endDateTime) < now).length,
    };
  }, [liveTrainingSessions]);

  // Create filter items for multi-select dropdowns
  const createFilterItems = (options: readonly string[], selected: string[]): Record<string, FilterItem> => {
    return options.reduce(
      (acc, option) => ({
        ...acc,
        [option]: {
          isSelected: selected.includes(option),
          isDisabled: false,
        },
      }),
      {} as Record<string, FilterItem>,
    );
  };

  const statusFilterItems = createFilterItems(
    [STATUS_FILTERS.ENROLLED, STATUS_FILTERS.COMPLETED, STATUS_FILTERS.SAVED],
    selectedStatusFilters,
  );
  const typeFilterItems = createFilterItems([TYPE_FILTERS.PUBLIC, TYPE_FILTERS.PRIVATE], selectedTypeFilters);
  const dateFilterItems = createFilterItems([DATE_FILTERS.UPCOMING, DATE_FILTERS.PAST], selectedDateFilters);

  // Handle filter changes
  const handleStatusFilterChange = (label: string, isCurrentlySelected: boolean): void => {
    const newFilters = isCurrentlySelected
      ? selectedStatusFilters.filter((filter) => filter !== label)
      : [...selectedStatusFilters, label];

    const newSearchParams = new URLSearchParams(searchParams);

    if (newFilters.length > 0) {
      newSearchParams.set(QUERY_PARAM_KEYS.STATUS, newFilters.join(','));
    } else {
      newSearchParams.delete(QUERY_PARAM_KEYS.STATUS);
    }
    navigate({ search: newSearchParams.toString() });
  };

  const handleTypeFilterChange = (label: string, isCurrentlySelected: boolean): void => {
    const newFilters = isCurrentlySelected
      ? selectedTypeFilters.filter((filter) => filter !== label)
      : [...selectedTypeFilters, label];

    const newSearchParams = new URLSearchParams(searchParams);

    if (newFilters.length > 0) {
      newSearchParams.set(QUERY_PARAM_KEYS.TYPE, newFilters.join(','));
    } else {
      newSearchParams.delete(QUERY_PARAM_KEYS.TYPE);
    }
    navigate({ search: newSearchParams.toString() });
  };

  const handleDateFilterChange = (label: string, isCurrentlySelected: boolean): void => {
    const newFilters = isCurrentlySelected
      ? selectedDateFilters.filter((filter) => filter !== label)
      : [...selectedDateFilters, label];

    const newSearchParams = new URLSearchParams(searchParams);

    if (newFilters.length > 0) {
      newSearchParams.set(QUERY_PARAM_KEYS.DATE, newFilters.join(','));
    } else {
      newSearchParams.delete(QUERY_PARAM_KEYS.DATE);
    }
    navigate({ search: newSearchParams.toString() });
  };

  const handleClearFilters = (): void => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.delete(QUERY_PARAM_KEYS.STATUS);
    newSearchParams.delete(QUERY_PARAM_KEYS.TYPE);
    newSearchParams.delete(QUERY_PARAM_KEYS.DATE);
    navigate({ search: newSearchParams.toString() });
  };

  // Mobile filter sections configuration with counts
  const mobileFilterSections: FilterSectionConfig[] = [
    {
      key: QUERY_PARAM_KEYS.STATUS,
      title: 'Status',
      options: [
        { label: STATUS_FILTERS.ENROLLED, count: filterCounts.enrolled },
        { label: STATUS_FILTERS.COMPLETED, count: filterCounts.completed },
        { label: STATUS_FILTERS.SAVED, count: filterCounts.saved },
      ],
    },
    {
      key: QUERY_PARAM_KEYS.TYPE,
      title: 'Type',
      options: [
        { label: TYPE_FILTERS.PUBLIC, count: filterCounts.public },
        { label: TYPE_FILTERS.PRIVATE, count: filterCounts.private },
      ],
    },
    {
      key: QUERY_PARAM_KEYS.DATE,
      title: 'Date',
      options: [
        { label: DATE_FILTERS.UPCOMING, count: filterCounts.upcoming },
        { label: DATE_FILTERS.PAST, count: filterCounts.past },
      ],
    },
  ];

  return {
    selectedStatusFilters,
    selectedTypeFilters,
    selectedDateFilters,
    selectedFiltersLength,
    hasActiveFilters,
    statusFilterItems,
    typeFilterItems,
    dateFilterItems,
    handleStatusFilterChange,
    handleTypeFilterChange,
    handleDateFilterChange,
    handleClearFilters,
    mobileFilterSections,
  };
};
